import React from "react";
import { useFormContext } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { X } from "lucide-react";
import { useGetAiOutcomesQuery } from "@/store/rest_api";
import { AVAILABLE_AI_TOOLS, isToolEnabled, toggleAgentTool } from "@/model/interface/AiAgent";
import SkillsPicker from "../components/SkillsPicker";
import { TemplateFormValues } from "../templateForm";

export default function TemplateSkillsToolsSection() {
  const form = useFormContext<TemplateFormValues>();
  const { data: availableOutcomes } = useGetAiOutcomesQuery();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Skills, Tools and Outcomes</CardTitle>
        <CardDescription>What the agent can do on a call, and what it can record when it is done</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField control={form.control} name="defaultSkills" render={({ field }) => (
          <FormItem>
            <FormLabel>Skills</FormLabel>
            <SkillsPicker
              selectedSkillIds={field.value ?? []}
              onChange={field.onChange}
              ownGroupLabel="This template"
              ownGroupHint="every agent on this template gets these"
            />
            <FormDescription>
              Capability blocks appended to the prompt, in this order. A single agent can get extra skills on
              its Configure page.
            </FormDescription>
          </FormItem>
        )} />

        <FormField control={form.control} name="tools" render={({ field }) => {
          const enabled = field.value ?? {};
          return (
            <FormItem>
              <FormLabel>Enabled Tools</FormLabel>
              <div className="space-y-2 rounded-md border p-3">
                {AVAILABLE_AI_TOOLS.map((tool) => (
                  <label key={tool.name} className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={isToolEnabled(enabled, tool.name)}
                      onCheckedChange={(checked) =>
                        field.onChange(toggleAgentTool(enabled, tool.name, checked === true))
                      }
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium">{tool.label}</span>
                      <span className="text-xs text-muted-foreground">{tool.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <FormDescription>
                Tools agents created from this template can call. <code>transfer_call</code> also needs a transfer
                number configured on the agent (Deployed Agents / org settings).
              </FormDescription>
            </FormItem>
          );
        }} />

        <FormField control={form.control} name="defaultDeclaredOutcomes" render={({ field }) => {
          const selected = field.value ?? [];
          return (
            <FormItem>
              <FormLabel>Default Declared Outcomes</FormLabel>
              <Select value="" onValueChange={(outcome) => { if (!selected.includes(outcome)) field.onChange([...selected, outcome]); }}>
                <FormControl>
                  <SelectTrigger><SelectValue placeholder="Add outcome..." /></SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availableOutcomes
                    ?.filter((outcome) => !selected.includes(outcome))
                    .map((outcome) => <SelectItem key={outcome} value={outcome}>{outcome}</SelectItem>)}
                </SelectContent>
              </Select>
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {selected.map((outcome) => (
                    <Badge key={outcome} variant="secondary" className="gap-1">
                      {outcome}
                      <button type="button" onClick={() => field.onChange(selected.filter((entry) => entry !== outcome))}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <FormDescription>
                Outcomes seeded onto agents created from this template. This is what <code>set_outcome</code> can set
                and what drives workflow routing.
              </FormDescription>
            </FormItem>
          );
        }} />
      </CardContent>
    </Card>
  );
}
